import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';

import { Link } from 'react-router-dom';

import './stylesheet.css';

import {
  dayKey,
  dayWindowFraction,
  DEFAULT_DAY_END_HOUR,
  DEFAULT_DAY_START_HOUR,
  isKept,
  keptRun,
  MAX_CONTRACT_POINTS,
  negotiateContract,
  paceFor,
  reliability,
  SUGGESTED_CONTRACT_POINTS,
  summarizeDays,
  togglePoint,
} from '../../lib/contract';
import {
  DEFAULT_ZUSAGE,
  extractContractFromFile,
  hasStrandDeclaration,
  upsertContractNode,
  upsertStrandDeclaration,
} from '../../lib/contract_org';
import { dashboardConfigFromState } from '../../lib/dashboard';
// Reused rather than duplicated: the window of daily-note dates and paths is exactly
// what the week dashboard already computes over the same dailyPathTemplate setting.
import { dailyFoldersForWindow, dailyPathsForWindow, windowOfDays } from '../../lib/week_dashboard';

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// Today plus the seven days the "way to success" row shows.
const WINDOW_DAYS = 8;

const percent = (fraction) => `${Math.round(fraction * 100)}%`;

const ContractDashboard = ({ syncBackendClient, storedConfig, startHour, endHour }) => {
  const config = useMemo(() => dashboardConfigFromState(storedConfig), [storedConfig]);

  // `today` is fixed at mount, exactly like the week dashboard: a page left open
  // across midnight keeps showing the day it was opened on, until refreshed. The
  // clock that runs the pace bar is separate (`now`, below) and ticks on its own -
  // conflating the two would reload the whole week every minute.
  const today = useMemo(() => new Date(), []);
  const todayKey = dayKey(today);
  const todayPath = useMemo(
    () => dailyPathsForWindow(config.dailyPathTemplate, today, WINDOW_DAYS).slice(-1)[0].path,
    [config.dailyPathTemplate, today]
  );

  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const [todayContract, setTodayContract] = useState(null);
  const [contractsByKey, setContractsByKey] = useState({});
  const [loaded, setLoaded] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [saveError, setSaveError] = useState(null);
  const [reloadToken, setReloadToken] = useState(0);
  const [draft, setDraft] = useState(null);
  // The Zusage of the Tagesvertrag strand, offered for editing only while the
  // strand is still undeclared - after that it is the user's line in gtd.org,
  // and the Kontrakt editor in the org view is where it gets changed.
  const [zusage, setZusage] = useState(DEFAULT_ZUSAGE);
  // null = not known yet (gtd.org unreadable): then nothing is declared, which
  // is survivable - a :KONTRAKT: naming an undeclared strand stays visible
  // rather than swallowing the node.
  const [strandDeclared, setStrandDeclared] = useState(null);
  // Which of the past seven days (if any) is showing its points for correction -
  // the "way to success" row itself only ever shows a mark, never an editor.
  const [expandedKey, setExpandedKey] = useState(null);

  // The raw text of today's note, so a write only ever replaces the Tagesvertrag
  // drawer and leaves the rest of the file exactly as it was. Kept in a ref (not
  // state) so two quick toggles both build on the write that came just before them.
  const rawRef = useRef(null);
  const existsRef = useRef(false);
  // Same idea, one entry per past day: { contents, exists } keyed by dayKey. A
  // past day is corrected far less often than today, but the risk two quick
  // toggles racing on stale contents is identical, so it gets the same treatment.
  const rawByKeyRef = useRef({});
  // Chained so two quick ticks become two orderly commits instead of two racing
  // ones - the GitLab commit API rejects concurrent branch updates.
  const queueRef = useRef(Promise.resolve());
  // The GTD file's raw contents, kept only so the strand declaration can be
  // appended to it once without re-reading.
  const gtdRef = useRef(null);

  // date/key/path for the seven days the week row shows - reused by the load and
  // by the past-day toggle handler, so the two can never disagree on a day's path.
  const pastWindow = useMemo(() => {
    const dates = windowOfDays(today, WINDOW_DAYS).slice(0, -1);
    const paths = dailyPathsForWindow(config.dailyPathTemplate, today, WINDOW_DAYS).slice(0, -1);
    return dates.map((date, index) => ({ date, key: dayKey(date), path: paths[index].path }));
  }, [today, config.dailyPathTemplate]);

  const load = useCallback(async () => {
    if (!syncBackendClient) {
      setLoadError('Not connected to a sync backend.');
      setLoaded(true);
      return;
    }

    setLoadError(null);
    setLoaded(false);

    const windowDates = windowOfDays(today, WINDOW_DAYS);
    const paths = dailyPathsForWindow(config.dailyPathTemplate, today, WINDOW_DAYS);
    const folders = dailyFoldersForWindow(config.dailyPathTemplate, today, WINDOW_DAYS);

    try {
      // Listed first, like the week dashboard: knowing which notes exist avoids a
      // failed fetch for every day that was never started.
      const listings = await Promise.all(
        folders.map((folder) => syncBackendClient.getDirectoryListing(folder).catch(() => null))
      );

      const present = new Set();
      listings.forEach((listing) => {
        if (!listing) return;
        (listing.listing || []).forEach((file) => {
          const filePath = file.get ? file.get('path') : file.path;
          if (filePath) present.add(filePath);
        });
      });

      const fetched = await Promise.all(
        paths.map(async ({ path }) => {
          if (!present.has(path)) return null;
          try {
            return await syncBackendClient.getFileContents(path);
          } catch (fileError) {
            return null;
          }
        })
      );

      const byKey = {};
      const rawByKey = {};
      windowDates.slice(0, -1).forEach((date, index) => {
        const key = dayKey(date);
        byKey[key] = extractContractFromFile(fetched[index]);
        rawByKey[key] = { contents: fetched[index], exists: fetched[index] !== null };
      });
      rawByKeyRef.current = rawByKey;

      const todayContents = fetched[fetched.length - 1];
      rawRef.current = todayContents;
      existsRef.current = todayContents !== null;

      // One extra read: whether the Tagesvertrag strand is declared yet. A
      // failure here is not a failure of the dashboard - it only means the
      // declaration is left alone.
      try {
        const gtdContents = await syncBackendClient.getFileContents(config.gtdPath);
        gtdRef.current = gtdContents;
        setStrandDeclared(hasStrandDeclaration(gtdContents));
      } catch (gtdError) {
        gtdRef.current = null;
        setStrandDeclared(null);
      }

      setContractsByKey(byKey);
      setTodayContract(extractContractFromFile(todayContents));
      setLoaded(true);
    } catch (error) {
      console.error('Could not build the contract dashboard:', error);
      setLoadError(error.message);
      setContractsByKey({});
      setTodayContract(null);
      setLoaded(true);
    }
  }, [syncBackendClient, config.dailyPathTemplate, config.gtdPath, today]);

  useEffect(() => {
    load();
  }, [load, reloadToken]);

  // Shared by today (via `persist` below) and by the past-day corrections: only
  // *where* the raw contents/existence flag live differs (a ref each vs. one ref
  // keyed by day), the write itself is identical either way.
  const persistDay = useCallback(
    (key, date, path, nextContract) => {
      const isToday = key === todayKey;

      if (isToday) {
        setTodayContract(nextContract);
      } else {
        setContractsByKey((previous) => ({ ...previous, [key]: nextContract }));
      }
      setSaveError(null);

      const entry = isToday
        ? { contents: rawRef.current, exists: existsRef.current }
        : rawByKeyRef.current[key] || { contents: null, exists: false };

      const nextContents = upsertContractNode(entry.contents, nextContract, date);

      if (isToday) {
        rawRef.current = nextContents;
      } else {
        rawByKeyRef.current[key] = { contents: nextContents, exists: entry.exists };
      }

      queueRef.current = queueRef.current.then(async () => {
        try {
          if (entry.exists) {
            await syncBackendClient.updateFile(path, nextContents);
          } else {
            await syncBackendClient.createFile(path, nextContents);
            if (isToday) {
              existsRef.current = true;
            } else if (rawByKeyRef.current[key]) {
              rawByKeyRef.current[key].exists = true;
            }
          }
        } catch (error) {
          setSaveError(`Could not save ${path}: ${error.message || error}`);
        }
      });
    },
    [syncBackendClient, todayKey]
  );

  const persist = useCallback(
    (nextContract) => persistDay(todayKey, today, todayPath, nextContract),
    [persistDay, todayKey, today, todayPath]
  );

  const canWrite = !!syncBackendClient;

  const summaries = useMemo(
    () => summarizeDays(contractsByKey, today),
    [contractsByKey, today]
  );
  const { negotiatedCount, keptCount } = useMemo(() => reliability(summaries), [summaries]);
  const run = useMemo(() => keptRun(summaries, todayContract), [summaries, todayContract]);

  const fraction = dayWindowFraction(now, startHour, endHour);
  const pace = paceFor(todayContract, fraction);

  const handleToggle = (index) => {
    if (!canWrite) return;
    persist(togglePoint(todayContract, index, Date.now()));
  };

  // Correcting a past day: a mistick fixed today is still an honest record
  // (concept §2.3) - the CLOSED timestamp it gets is the moment of the
  // correction, not a backdated guess, and git still shows exactly when it
  // was made. Only ticking/unticking existing points is offered here; the
  // tool still never drafts or backfills points for a day that had none.
  const expandedDay = useMemo(
    () => pastWindow.find((day) => day.key === expandedKey) || null,
    [pastWindow, expandedKey]
  );
  const expandedContract = expandedDay ? contractsByKey[expandedDay.key] : null;

  const toggleExpanded = (key) => setExpandedKey((current) => (current === key ? null : key));

  const handleTogglePastPoint = (day, index) => {
    if (!canWrite) return;
    const next = togglePoint(contractsByKey[day.key], index, Date.now());
    if (!next) return;
    persistDay(day.key, day.date, day.path, next);
  };

  // Declaring the strand is a once-ever act, done on the first signature that
  // finds it missing. The user writes the Zusage (§2.6: the tool is the notary,
  // not the counterparty); organice only files it. Queued behind the day's own
  // write so two commits do not race on the same branch.
  const declareStrand = useCallback(() => {
    if (strandDeclared !== false || gtdRef.current === null) return;

    const nextContents = upsertStrandDeclaration(gtdRef.current, zusage);
    if (nextContents === gtdRef.current) return;

    gtdRef.current = nextContents;
    setStrandDeclared(true);

    queueRef.current = queueRef.current.then(async () => {
      try {
        await syncBackendClient.updateFile(config.gtdPath, nextContents);
      } catch (error) {
        // The day's contract is written either way - it just names a strand
        // nobody has declared yet, which stays visible instead of vanishing.
        setSaveError(`Could not declare the Tagesvertrag strand in ${config.gtdPath}: ${
          error.message || error
        }`);
        setStrandDeclared(false);
      }
    });
  }, [strandDeclared, zusage, syncBackendClient, config.gtdPath]);

  const handleSign = () => {
    if (!canWrite || !draft) return;
    const next = negotiateContract(todayContract, { points: draft, now: Date.now() });
    if (!next) return;

    persist(next);
    declareStrand();
    setDraft(null);
  };

  const openEditor = () =>
    setDraft(
      todayContract
        ? todayContract.points.map((point) => point.text)
        : Array.from({ length: SUGGESTED_CONTRACT_POINTS }, () => '')
    );

  const draftHasContent = !!draft && draft.some((text) => !!text.trim());

  const header = (
    <div className="contract__header">
      <h3 className="contract__title">Contract</h3>
      <div className="contract__header__actions">
        <button className="contract__header__action" onClick={() => setReloadToken((n) => n + 1)}>
          <i className="fas fa-sync-alt" /> Refresh
        </button>
        <Link className="contract__header__action" to="/dashboard/week">
          <i className="fas fa-chart-line" /> This week
        </Link>
        <Link className="contract__header__action" to="/dashboard/focus">
          <i className="fas fa-bullseye" /> Focus
        </Link>
        <Link className="contract__header__action" to="/dashboard/team">
          <i className="fas fa-users" /> Team
        </Link>
        <Link className="contract__header__action" to="/dashboard/formation">
          <i className="fas fa-seedling" /> Formation
        </Link>
        <Link className="contract__header__action" to="/dashboard/review">
          <i className="fas fa-moon" /> Review
        </Link>
        <Link className="contract__header__action" to="/dashboard">
          <i className="fas fa-th-large" /> Dashboard
        </Link>
      </div>
    </div>
  );

  if (!loaded) {
    return (
      <div className="contract-container">
        {header}
        <div className="contract__loading">
          <i className="fas fa-spinner fa-spin" /> Reading {todayPath}...
        </div>
      </div>
    );
  }

  const statusLine = !todayContract
    ? null
    : pace.status === 'kept'
    ? `Kept — all ${pace.pointCount} point${pace.pointCount === 1 ? '' : 's'} done.`
    : fraction === 0
    ? 'Nothing due yet — the working day has not started.'
    : pace.status === 'onPace'
    ? `On pace: ${pace.doneCount} of ${pace.pointCount} done, ${percent(fraction)} of the day passed.`
    : fraction === 1
    ? `Not kept yet — ${pace.pointCount - pace.doneCount} point${
        pace.pointCount - pace.doneCount === 1 ? '' : 's'
      } still open today.`
    : `${pace.behindBy} point${pace.behindBy === 1 ? '' : 's'} behind the day.`;

  return (
    <div className="contract-container">
      {header}

      {!!loadError && <div className="contract__error">{loadError}</div>}
      {!!saveError && <div className="contract__error">{saveError}</div>}

      <div className="contract__section-title">
        <span>Today — {todayPath}</span>
        {!!todayContract && !draft && canWrite && (
          <button className="contract__section-title__action" onClick={openEditor}>
            <i className="fas fa-pen-alt" /> Renegotiate
          </button>
        )}
      </div>

      {draft ? (
        <div className="contract__editor">
          <div className="contract__editor__hint">
            A few specific points — phrased as actions, each checkable tonight. Signed points are
            saved as <code>TODO</code> subtasks under <code>* Tagesvertrag</code> in{' '}
            <code>{todayPath}</code>, one commit per change.
          </div>

          {draft.map((text, index) => (
            <div className="contract__editor__row" key={index}>
              <input
                className="textfield"
                type="text"
                value={text}
                placeholder={index === 0 ? 'After standup: write the release notes' : ''}
                autoFocus={index === 0}
                onChange={(event) =>
                  setDraft(draft.map((line, i) => (i === index ? event.target.value : line)))
                }
              />
              {draft.length > 1 && (
                <button
                  className="contract__editor__remove"
                  title="Remove this point"
                  onClick={() => setDraft(draft.filter((_line, i) => i !== index))}
                >
                  <i className="fas fa-times" />
                </button>
              )}
            </div>
          ))}

          {strandDeclared === false && (
            <div className="contract__zusage">
              <label className="contract__zusage__label">
                Zusage des Tagesvertrags
                <span className="contract__zusage__once"> — einmalig, beim ersten Vertrag</span>
              </label>
              <input
                className="textfield"
                type="text"
                value={zusage}
                onChange={(event) => setZusage(event.target.value)}
              />
              <div className="contract__editor__hint">
                Signing declares the <code>tagesvertrag</code> Kontrakt in <code>{config.gtdPath}</code>,
                so every day's points belong to one strand rather than to a collector. This sentence
                is the promise it carries — yours to word, and afterwards editable on that headline
                like any other Kontrakt.
              </div>
            </div>
          )}

          <div className="contract__editor__actions">
            <button className="contract__sign" disabled={!draftHasContent} onClick={handleSign}>
              <i className="fas fa-file-signature" />{' '}
              {todayContract ? 'Renegotiate' : 'Sign for today'}
            </button>
            {draft.length < MAX_CONTRACT_POINTS && (
              <button className="btn btn-passive" onClick={() => setDraft([...draft, ''])}>
                Add point
              </button>
            )}
            <button className="btn btn-passive" onClick={() => setDraft(null)}>
              Cancel
            </button>
          </div>
        </div>
      ) : todayContract ? (
        <>
          <div className="contract__points">
            {todayContract.points.map((point, index) => (
              <button
                className={`contract__point${point.done ? ' contract__point--done' : ''}`}
                key={index}
                disabled={!canWrite}
                onClick={() => handleToggle(index)}
              >
                <span className="contract__point__box">{point.done ? '[X]' : '[ ]'}</span>
                <span className="contract__point__text">{point.text}</span>
              </button>
            ))}
          </div>

          {todayContract.renegotiations > 0 && (
            <div className="contract__meta">renegotiated ×{todayContract.renegotiations}</div>
          )}

          <div
            className={`contract__pace${
              pace.status === 'kept'
                ? ' contract__pace--kept'
                : pace.status === 'behind'
                ? ' contract__pace--behind'
                : ''
            }`}
          >
            <div className="contract__pace__status">{statusLine}</div>
            <div className="contract__pace__bars">
              <div className="contract__pace__row">
                <span>day</span>
                <div className="contract__bar">
                  <div className="contract__bar__fill" style={{ width: percent(fraction) }} />
                </div>
                <strong>{percent(fraction)}</strong>
              </div>
              <div className="contract__pace__row">
                <span>points</span>
                <div className="contract__bar">
                  <div
                    className="contract__bar__fill contract__bar__fill--points"
                    style={{
                      width: percent(pace.pointCount ? pace.doneCount / pace.pointCount : 0),
                    }}
                  />
                </div>
                <strong>
                  {pace.doneCount}/{pace.pointCount}
                </strong>
              </div>
            </div>
          </div>
        </>
      ) : (
        <div className="contract__editor">
          <div className="contract__empty">No contract for today yet.</div>
          {canWrite ? (
            <div>
              <button className="contract__sign" onClick={openEditor}>
                <i className="fas fa-file-signature" /> Negotiate today's contract
              </button>
            </div>
          ) : (
            !loadError && <div className="contract__empty">Sign in to a sync backend first.</div>
          )}
        </div>
      )}

      <div className="contract__section-title">
        <span>The way to success</span>
      </div>

      <div className="contract__week">
        {summaries.map((summary) => {
          const dayMark = (
            <>
              <span
                className={`contract__week__mark${
                  summary.kept
                    ? ' contract__week__mark--kept'
                    : summary.negotiated
                    ? ' contract__week__mark--broken'
                    : ''
                }`}
              >
                {summary.kept ? '✓' : summary.negotiated ? '✗' : '—'}
              </span>
              <span className="contract__week__counts">
                {summary.negotiated ? `${summary.doneCount}/${summary.pointCount}` : ' '}
              </span>
              <span className="contract__week__label">{WEEKDAYS[summary.date.getDay()]}</span>
            </>
          );

          // Only a negotiated day has points to correct - a rest day ('—') stays
          // a plain, non-interactive cell, exactly as before.
          return canWrite && summary.negotiated ? (
            <button
              type="button"
              className={`contract__week__day contract__week__day--editable${
                expandedKey === summary.key ? ' contract__week__day--expanded' : ''
              }`}
              key={summary.key}
              title={`${summary.key} — tap to correct`}
              onClick={() => toggleExpanded(summary.key)}
            >
              {dayMark}
            </button>
          ) : (
            <div className="contract__week__day" key={summary.key} title={summary.key}>
              {dayMark}
            </div>
          );
        })}

        <div className="contract__week__day contract__week__day--today" title={todayKey}>
          <span
            className={`contract__week__mark${
              isKept(todayContract) ? ' contract__week__mark--kept' : ''
            }`}
          >
            {isKept(todayContract) ? '✓' : todayContract ? '…' : '—'}
          </span>
          <span className="contract__week__counts">
            {todayContract ? `${pace.doneCount}/${pace.pointCount}` : ' '}
          </span>
          <span className="contract__week__label">today</span>
        </div>
      </div>

      {!!expandedDay && !!expandedContract && (
        <div className="contract__past-day">
          <div className="contract__section-title">
            <span>
              Correcting {WEEKDAYS[expandedDay.date.getDay()]} {expandedDay.key} —{' '}
              {expandedDay.path}
            </span>
            <button
              className="contract__section-title__action"
              onClick={() => setExpandedKey(null)}
            >
              <i className="fas fa-times" /> Close
            </button>
          </div>
          <div className="contract__points">
            {expandedContract.points.map((point, index) => (
              <button
                className={`contract__point${point.done ? ' contract__point--done' : ''}`}
                key={index}
                disabled={!canWrite}
                onClick={() => handleTogglePastPoint(expandedDay, index)}
              >
                <span className="contract__point__box">{point.done ? '[X]' : '[ ]'}</span>
                <span className="contract__point__text">{point.text}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="contract__numbers">
        <strong>{negotiatedCount + (todayContract ? 1 : 0)}</strong> of 8 days under contract ·{' '}
        <strong>{keptCount + (isKept(todayContract) ? 1 : 0)}</strong> kept
        {run > 0 && (
          <>
            {' '}
            · run of <strong>{run}</strong>
          </>
        )}
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  syncBackendClient: state.syncBackend.get('client'),
  storedConfig: state.base.get('dashboardConfig'),
  startHour:
    state.base.get('contractDayStartHour') === undefined
      ? DEFAULT_DAY_START_HOUR
      : state.base.get('contractDayStartHour'),
  endHour:
    state.base.get('contractDayEndHour') === undefined
      ? DEFAULT_DAY_END_HOUR
      : state.base.get('contractDayEndHour'),
});

export default connect(mapStateToProps)(ContractDashboard);
